import UpdateButton from "../../shared/UpdateButton/UpdateButton";
import {useEffect, useState} from "react";
import {
  Checkbox,
  IconButton,
  Input,
  InputGroup,
  toaster,
  Toggle,
  Tooltip,
  Whisper
} from "rsuite";
import "./NetworkSettings.scss";
import {
  EthernetParams,
  NetworkSettingsProps,
  SimParams,
  WifiParams, WifiState
} from "./NetworkSetting.model";
import StatusControl from "../../shared/StatusControl/StatusControl";
import {NetworkSettingsApi} from "../../../services/NetworkSettings.api";
import NotificationFactory from "../../../shared/Notifications";
import {
  AiOutlineEyeIcon,
  AiOutlineEyeInvisibleIcon,
  BsBootstrapRebootIcon,
  IoCloseIcon,
  TfiSaveIcon
} from "../../../models/Icons.model";

const NetworkSettings = ({executeCommand, deviceId}: NetworkSettingsProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [networksList, setNetworksList] = useState<{ label: string; value: string; key: string }[]>([]);
  const [wifiNetworksLoading, setWifiNetworksLoading] = useState<boolean>(false);
  const [hasLoadedNetworks, setHasLoadedNetworks] = useState<boolean>(false);
  const [selectedNetwork, setSelectedNetwork] = useState<string>("");
  const [wifiParams, setWifiParams] = useState<WifiParams | null>(null);
  const [wifiState, setWifiState] = useState<WifiState | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showSimPassword, setShowSimPassword] = useState<boolean>(false);

  const [ip, setIp] = useState<string>("");
  const [mask, setMask] = useState<string>("");
  const [gateway, setGateway] = useState<string>("");
  const [dns, setDns] = useState<string>("");
  const [dhcpEnabled, setDhcpEnabled] = useState<boolean>(false);

  const [simParams, setSimParams] = useState<SimParams | null>(null);


  const updateFormField = <K extends keyof WifiParams>(
    field: K,
    value: WifiParams[K]
  ) => {
    setWifiParams(prev => {
      const baseEthernet = {ip: "", mask: "", gateway: "", dns: ""};
      const base: WifiParams = {
        ssid: "",
        password: "",
        dhcpEnabled: false,
        ethernetSettings: baseEthernet
      };
      const current = prev || base;
      return {
        ...current,
        [field]: value
      };
    });
  };

  const updateEthernetField = <K extends keyof NonNullable<WifiParams["ethernetSettings"]>>(
    field: K,
    value: string
  ) => {
    setWifiParams(prev => {
      const baseEthernet = {ip: "", mask: "", gateway: "", dns: ""};
      const base: WifiParams = {
        ssid: "",
        password: "",
        dhcpEnabled: false,
        ethernetSettings: baseEthernet
      };
      const current = prev || base;
      const ethernetSettings = current.ethernetSettings || baseEthernet;

      return {
        ...current,
        ethernetSettings: {
          ...ethernetSettings,
          [field]: value
        }
      };
    });
  };

  useEffect(() => {
    initialLoad().then();
  }, [deviceId]);

  useEffect(() => {
    setNetworksList([]);
    setSelectedNetwork("");
    setHasLoadedNetworks(false);
  }, [deviceId]);

  const initialLoad = async () => {
    try {
      setLoading(true);
      await getWifiParams();
      await getWifiStatus();
      await getEthernetParams();
      await getSimParams();
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = async () => {
    try {
      setLoading(true);
      await Promise.all([
        getWifiParams(),
        getWifiStatus(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getWifiParams = async () => {
    try {
      const response: WifiParams = await NetworkSettingsApi.getWifiParams(deviceId);
      setWifiParams(response);
    } catch (e) {
      console.error(e);
    }
  };

  const getWifiStatus = async () => {
    try {
      const response: WifiState = await NetworkSettingsApi.getWifiStatus(deviceId);
      setWifiState(response);
    } catch (e) {
      console.error(e);
    }
  };
  const getNetworksList = async () => {
    setWifiNetworksLoading(true);

    try {
      const result = await NetworkSettingsApi.getNetworksList(deviceId);
      const networks = Array.isArray(result?.networks)
        ? result.networks.map((net: string, index: number) => ({
          label: net,
          value: net,
          key: `${net}-${index}`
        }))
        : [];

      setNetworksList(networks);
      setHasLoadedNetworks(true);
    } catch (e) {
      console.error(e);
    } finally {
      setWifiNetworksLoading(false);
    }
  };

  const handleOpenNetworksList = async () => {
    if (wifiNetworksLoading || hasLoadedNetworks) {
      return;
    }

    await getNetworksList();
  };

  const updateWifiParams = async () => {
      if (!wifiParams) return;

      try {
        setLoading(true);

        const payload: WifiParams = {
          ssid: wifiParams.ssid || "",
          password: wifiParams.password || "",
          dhcpEnabled: wifiParams.dhcpEnabled,
          ethernetSettings: {
            ip: wifiParams.ethernetSettings?.ip || "",
            mask: wifiParams.ethernetSettings?.mask || "",
            gateway: wifiParams.ethernetSettings?.gateway || "",
            dns: wifiParams.ethernetSettings?.dns || ""
          }
        }

        const result = await NetworkSettingsApi.updateWifiParams(deviceId, payload);

        if (result) {
          toaster.push(NotificationFactory.success(), {
            placement: "topEnd",
            duration: 3000
          });
          getWifiParams().then()
        }
      } catch
        (e) {
        console.error(e);
        toaster.push(NotificationFactory.error(), {
          placement: "topEnd",
          duration: 3000
        });
      } finally {
        setLoading(false);
      }
    }
  ;

  const handleClear = (setter: (v: string) => void) => {
    setter("");
  };

  const getEthernetParams = async () => {
    setLoading(true);
    try {
      const response: EthernetParams = await NetworkSettingsApi.getEthernetParams(deviceId);
      setEthernetParams(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getSimParams = async () => {
    try {
      const response = await NetworkSettingsApi.getSimParams(deviceId);
      setSimParams(response);
    } catch (error) {
      console.error(error);
    }
  };

  const updateSimParams = async () => {
    setLoading(true);
    const payload: SimParams = {
      apn: simParams?.apn || "",
      login: simParams?.login || "",
      password: simParams?.password || ""
    };

    try {
      const result = await NetworkSettingsApi.updateSimParams(deviceId, payload);
      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });
      }
    } catch (e) {
      console.error(e);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const restartSim = async () => {
    setLoading(true);

    try {
      const result = await NetworkSettingsApi.restartSim(deviceId);
      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });
      }
    } catch (e) {
      console.error(e);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const setEthernetParams = (ethernetState: EthernetParams) => {
    if (ethernetState && ethernetState.ethernetSettings) {
      setDhcpEnabled(ethernetState.dhcpEnabled);
      setIp(ethernetState.ethernetSettings.ip || "");
      setMask(ethernetState.ethernetSettings.mask || "");
      setGateway(ethernetState.ethernetSettings.gateway || "");
      setDns(ethernetState.ethernetSettings.dns || "");
    }
  };

  const updateEthernetState = async () => {
    setLoading(true);
    const payload: EthernetParams = dhcpEnabled
      ? {dhcpEnabled: true}
      : {
        dhcpEnabled: false,
        ethernetSettings: {
          ip: ip || "",
          mask: mask || "",
          gateway: gateway || "",
          dns: dns || ""
        }
      };

    try {
      const result = await NetworkSettingsApi.updateEthernetParams(deviceId, payload);
      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });
      }
    } catch (e) {
      console.error(e);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDhcpToggle = async (enabled: boolean) => {
    setLoading(true);
    try {
      const payload: EthernetParams = enabled
        ? {dhcpEnabled: true}
        : {
          dhcpEnabled: false,
          ethernetSettings: {
            ip: ip || "",
            mask: mask || "",
            gateway: gateway || "",
            dns: dns || ""
          }
        };

      const result = await NetworkSettingsApi.updateEthernetParams(deviceId, payload);

      if (result) {
        await getEthernetParams();
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });
      }
    } catch (e) {
      console.error(e);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">WiFi</div>
        <section className="secondary-clock">
          <div className="row p-2 pt-1">
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mt-3">
              <div className="fieldset-wrapper control h-100 bg-white mt-0 pt-2">
                <div className="legend-title">Состояние WiFi</div>
                <div className="row pb-0">
                  <div className="col-12 text-end">
                    <UpdateButton loading={loading} onRefresh={refreshAll}/>
                  </div>
                </div>
                <div className="clock-wrapper w-100">
                  <InputGroup className="mb-2">
                    <InputGroup.Addon style={{width: 55}}>SSID</InputGroup.Addon>
                    <Input value={wifiState?.ssid || ""} readOnly/>
                  </InputGroup>

                  <InputGroup className="mb-2">
                    <InputGroup.Addon style={{width: 55}}>IP</InputGroup.Addon>
                    <Input value={wifiState?.ip || ""} readOnly/>
                  </InputGroup>

                  <InputGroup className="mb-2">
                    <InputGroup.Addon style={{width: 55}}>MAC</InputGroup.Addon>
                    <Input value={wifiState?.mac || ""} readOnly/>
                  </InputGroup>

                  <InputGroup className="mb-2">
                    <InputGroup.Addon style={{width: 55}}>RSSI</InputGroup.Addon>
                    <Input value={wifiState?.rssi || ""} readOnly/>
                  </InputGroup>
                </div>
              </div>
            </div>
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mt-3">
              <div className="fieldset-wrapper control h-100 bg-white mt-0 pt-2">
                <div className="legend-title">Параметры WiFi</div>
                <div className="row">
                  <div className="col-12 text-end">
                    <Whisper placement="top" speaker={<Tooltip>Сохранить настройки WiFi</Tooltip>}>
                      <IconButton
                        style={{marginRight: "10px"}}
                        icon={<TfiSaveIcon/>}
                        size="sm"
                        loading={loading}
                        className="refresh-btn"
                        onClick={updateWifiParams}
                      />
                    </Whisper>
                    <UpdateButton loading={loading} onRefresh={refreshAll}/>
                  </div>
                </div>
                <div className="clock-wrapper w-100">
                  <InputGroup className="mb-2">
                    <InputGroup.Addon>SSID</InputGroup.Addon>
                    <Input
                      value={wifiParams?.ssid || ""}
                      onChange={(value) => updateFormField("ssid", value as string)}
                      disabled={loading}
                    />
                  </InputGroup>

                  <InputGroup className="mb-2">
                    <InputGroup.Addon>Пароль</InputGroup.Addon>
                    <Input
                      value={wifiParams?.password || ""}
                      onChange={(value) => updateFormField("password", value as string)}
                      type={showPassword ? "text" : "password"}
                      disabled={loading}
                    />
                    <InputGroup.Addon
                      style={{cursor: "pointer", display: "flex", alignItems: "center"}}
                      onClick={() => setShowPassword(prev => !prev)}
                    >
                      {showPassword ? <AiOutlineEyeIcon/> : <AiOutlineEyeInvisibleIcon/>}
                    </InputGroup.Addon>
                  </InputGroup>

                  <div className="d-flex align-items-center w-100 mb-2">
                    <Checkbox
                      checked={!!wifiParams?.dhcpEnabled}
                      onChange={(_val, checked) => updateFormField("dhcpEnabled", checked)}
                      disabled={loading}
                    />
                    <span style={{marginLeft: 8}}>DHCP</span>
                  </div>

                  {!wifiParams?.dhcpEnabled && wifiParams && (
                    <div className="w-100">
                      <InputGroup className="mb-2">
                        <InputGroup.Addon>IP</InputGroup.Addon>
                        <Input
                          placeholder="000.000.000.000"
                          disabled={loading}
                          value={wifiParams.ethernetSettings?.ip || ""}
                          onChange={(value) => updateEthernetField("ip", value as string)}
                        />
                        <InputGroup.Addon style={{cursor: "pointer"}}>
                          <IoCloseIcon/>
                        </InputGroup.Addon>
                      </InputGroup>

                      <InputGroup className="mb-2">
                        <InputGroup.Addon>Маска</InputGroup.Addon>
                        <Input
                          value={wifiParams.ethernetSettings?.mask || ""}
                          placeholder="000.000.000.000"
                          disabled={loading}
                          onChange={(value) => updateEthernetField("mask", value as string)}
                        />
                        <InputGroup.Addon style={{cursor: "pointer"}}>
                          <IoCloseIcon/>
                        </InputGroup.Addon>
                      </InputGroup>

                      <InputGroup className="mb-2">
                        <InputGroup.Addon>Шлюз</InputGroup.Addon>
                        <Input
                          value={wifiParams.ethernetSettings?.gateway || ""}
                          placeholder="000.000.000.000"
                          disabled={loading}
                          onChange={(value) => updateEthernetField("gateway", value as string)}
                        />
                        <InputGroup.Addon style={{cursor: "pointer"}}>
                          <IoCloseIcon/>
                        </InputGroup.Addon>
                      </InputGroup>

                      <InputGroup className="mb-2">
                        <InputGroup.Addon>DNS</InputGroup.Addon>
                        <Input
                          value={wifiParams.ethernetSettings?.dns || ""}
                          placeholder="000.000.000.000"
                          disabled={loading}
                          onChange={(value) => updateEthernetField("dns", value as string)}
                        />
                        <InputGroup.Addon style={{cursor: "pointer"}}>
                          <IoCloseIcon/>
                        </InputGroup.Addon>
                      </InputGroup>
                    </div>
                  )}

                  <InputGroup style={{padding: 0}}>
                    <InputGroup.Addon>Сети WIFI</InputGroup.Addon>
                    <div style={{width: "100%"}}>
                      <select
                        className="form-select"
                        style={{borderRadius: 0}}
                        value={selectedNetwork || ""}
                        onFocus={() => handleOpenNetworksList()}
                        onMouseDown={() => handleOpenNetworksList()}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedNetwork(value);
                          if (value) {
                            updateFormField("ssid", value);
                            updateFormField("password", "");
                          }
                        }}
                      >
                        <option value=""></option>
                        {(wifiNetworksLoading || loading) && (
                          <option value="" disabled>
                            Загрузка...
                          </option>
                        )}
                        {networksList.map((network) => (
                          <option key={network.key} value={network.value}>
                            {network.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </InputGroup>
                </div>
              </div>
            </div>


          </div>
        </section>
      </div>

      <div className="row mb-2">
        <div className="col-xxl-6 col-lg-6 col-md-6 col-sm-12">
          <div className="fieldset-wrapper">
            <div className="legend-title main-legend-title">LAN</div>
            <section className="secondary-clock">
              <div className="row p-2 pb-0">
                <div className="col-12 text-end">
                  <Whisper placement="top" speaker={<Tooltip>Сохранить настройки LAN</Tooltip>}>
                    <IconButton
                      style={{marginRight: "10px"}}
                      icon={<TfiSaveIcon/>}
                      size="sm"
                      loading={loading}
                      className="refresh-btn"
                      onClick={updateEthernetState}
                    />
                  </Whisper>
                  <UpdateButton loading={loading} onRefresh={getEthernetParams}/>
                </div>
              </div>

              <div className="row p-2">
                <div className="col-12">
                  <div className="row mb-3 w-100">
                    <div className="col-md-1">
                      <StatusControl status={dhcpEnabled ? "online" : "offline"}/>
                    </div>
                    <div className="col-md-11">
                      <Toggle
                        style={{marginTop: 5}}
                        size="md"
                        color="green"
                        checked={dhcpEnabled}
                        onChange={handleDhcpToggle}
                        loading={loading}
                      >
                        DHCP {dhcpEnabled ? "включен" : "выключен"}
                      </Toggle>
                    </div>
                  </div>

                  <div className="row w-100">
                    <div className="col-12">
                      <InputGroup className="mb-3">
                        <InputGroup.Addon>IP</InputGroup.Addon>
                        <Input
                          value={ip}
                          onChange={setIp}
                          placeholder="000.000.000.000"
                          disabled={dhcpEnabled || loading}
                        />
                        <InputGroup.Addon
                          style={{cursor: "pointer"}}
                          onClick={() => !dhcpEnabled && handleClear(setIp)}
                        >
                          <IoCloseIcon/>
                        </InputGroup.Addon>
                      </InputGroup>

                      <InputGroup className="mb-3">
                        <InputGroup.Addon>Маска</InputGroup.Addon>
                        <Input
                          value={mask}
                          onChange={setMask}
                          placeholder="000.000.000.000"
                          disabled={dhcpEnabled || loading}
                        />
                        <InputGroup.Addon
                          style={{cursor: "pointer"}}
                          onClick={() => !dhcpEnabled && handleClear(setMask)}
                        >
                          <IoCloseIcon/>
                        </InputGroup.Addon>
                      </InputGroup>

                      <InputGroup className="mb-3">
                        <InputGroup.Addon>Шлюз</InputGroup.Addon>
                        <Input
                          value={gateway}
                          onChange={setGateway}
                          placeholder="000.000.000.000"
                          disabled={dhcpEnabled || loading}
                        />
                        <InputGroup.Addon
                          style={{cursor: "pointer"}}
                          onClick={() => !dhcpEnabled && handleClear(setGateway)}
                        >
                          <IoCloseIcon/>
                        </InputGroup.Addon>
                      </InputGroup>

                      <InputGroup className="mb-3">
                        <InputGroup.Addon>DNS</InputGroup.Addon>
                        <Input
                          value={dns}
                          onChange={setDns}
                          placeholder="000.000.000.000"
                          disabled={dhcpEnabled || loading}
                        />
                        <InputGroup.Addon
                          style={{cursor: "pointer"}}
                          onClick={() => !dhcpEnabled && handleClear(setDns)}
                        >
                          <IoCloseIcon/>
                        </InputGroup.Addon>
                      </InputGroup>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="col-xxl-6 col-lg-6 col-md-6 col-sm-12">
          <div className="fieldset-wrapper">
            <div className="legend-title main-legend-title">SIM</div>
            <section className="secondary-clock">
              <div className="row p-2 pb-0">
                <div className="col-12 d-flex justify-content-between">
                  <Whisper placement="top" speaker={<Tooltip>Перезапуск контекста SIM</Tooltip>}>
                    <IconButton
                      style={{marginRight: "10px", color: "red"}}
                      icon={<BsBootstrapRebootIcon/>}
                      size="sm"
                      loading={loading}
                      className="refresh-btn"
                      onClick={restartSim}
                    />
                  </Whisper>
                  <div>
                    <Whisper placement="top" speaker={<Tooltip>Сохранить настройки SIM</Tooltip>}>
                      <IconButton
                        style={{marginRight: "10px"}}
                        icon={<TfiSaveIcon/>}
                        size="sm"
                        loading={loading}
                        className="refresh-btn"
                        onClick={updateSimParams}
                      />
                    </Whisper>
                    <UpdateButton loading={loading} onRefresh={getSimParams}/>
                  </div>
                </div>
              </div>
              <div className="row p-2 pb-0">
                <div className="col-12">
                  <InputGroup className="mb-3">
                    <InputGroup.Addon>APN</InputGroup.Addon>
                    <Input
                      value={simParams?.apn || ""}
                      onChange={(value) =>
                        setSimParams(prev => ({
                          ...(prev || {apn: "", login: "", password: ""}),
                          apn: value as string
                        }))
                      }
                      disabled={loading}
                    />
                  </InputGroup>
                  <InputGroup className="mb-3">
                    <InputGroup.Addon>Логин</InputGroup.Addon>
                    <Input
                      value={simParams?.login || ""}
                      onChange={(value) =>
                        setSimParams(prev => ({
                          ...(prev || {apn: "", login: "", password: ""}),
                          login: value as string
                        }))
                      }
                      disabled={loading}
                    />
                  </InputGroup>
                  <InputGroup className="mb-2">
                    <InputGroup.Addon>Пароль</InputGroup.Addon>
                    <Input
                      value={simParams?.password || ""}
                      onChange={(value) =>
                        setSimParams(prev => ({
                          ...(prev || {apn: "", login: "", password: ""}),
                          password: value as string
                        }))
                      }
                      type={showSimPassword ? "text" : "password"}
                      disabled={loading}
                    />
                    <InputGroup.Addon
                      style={{cursor: "pointer", display: "flex", alignItems: "center"}}
                      onClick={() => setShowSimPassword(prev => !prev)}
                    >
                      {showSimPassword ? <AiOutlineEyeIcon/> : <AiOutlineEyeInvisibleIcon/>}
                    </InputGroup.Addon>
                  </InputGroup>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default NetworkSettings;
